// Coach response parsing and formatting helpers

export interface Block {
  type: 'heading' | 'paragraph' | 'bullet' | 'suggestion'
  text: string
  source?: string
  target?: string
  rationale?: string
}

export function parseMarkdownBlocks(content: string): Block[] {
  const lines = content.split('\n')
  const blocks: Block[] = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue

    // Heading: **Text:** or ## Text
    if (/^\*\*[^*]+:\*\*$/.test(line) || /^#{1,3}\s/.test(line)) {
      const text = line.replace(/^\*\*/, '').replace(/:\*\*$/, '').replace(/^#{1,3}\s/, '')
      blocks.push({ type: 'heading', text })
      continue
    }

    // Suggestion: - "Source" → **Target** (rationale)
    const suggestionMatch = line.match(/^-\s*"([^"]+)"\s*→\s*\*{0,2}([^*(\n]+)\*{0,2}\s*(?:\+\s*\*{0,2}([^*(\n]+)\*{0,2})?\s*(?:[\(（]([^)）]+)[\)）])?/)
    if (suggestionMatch) {
      blocks.push({
        type: 'suggestion',
        text: line,
        source: suggestionMatch[1].trim(),
        target: (suggestionMatch[2].trim() + (suggestionMatch[3] ? ' + ' + suggestionMatch[3].trim() : '')).replace(/\*+/g, ''),
        rationale: suggestionMatch[4]?.trim(),
      })
      continue
    }

    // Bullet
    if (/^[-•]\s/.test(line)) {
      blocks.push({ type: 'bullet', text: line.replace(/^[-•]\s/, '') })
      continue
    }

    blocks.push({ type: 'paragraph', text: line })
  }

  return blocks
}

/** Convert inline markdown (**bold**, *italic*, `code`, [icon:name]) to HTML */
export function inlineMd(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-medium text-text">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="text-[11px] bg-warm-100 px-1 py-px rounded">$1</code>')
    .replace(/\[icon:([a-z-]+)\]/g, (_, name) => lucideIconSvg(name))
}

// Inline SVG Lucide icons (14px, matches text flow). Stroke-width 2, currentColor.
const LUCIDE_SVG: Record<string, string> = {
  'arrow-right': '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
  'target': '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>',
  'users': '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  'search': '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
  'lightbulb': '<path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/>',
  'alert-triangle': '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  'check-circle': '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/>',
  'file-text': '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>',
  'bar-chart': '<line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/>',
  'compass': '<circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>',
  'zap': '<path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/>',
  'message-circle': '<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>',
  'puzzle': '<path d="M19.439 7.85c-.049.322.059.648.289.878l1.568 1.568c.47.47.706 1.087.706 1.704s-.235 1.233-.706 1.704l-1.611 1.611a.98.98 0 0 1-.837.276c-.47-.07-.802-.48-.968-.925a2.501 2.501 0 1 0-3.214 3.214c.446.166.855.497.925.968a.979.979 0 0 1-.276.837l-1.61 1.61a2.404 2.404 0 0 1-1.705.707 2.402 2.402 0 0 1-1.704-.706l-1.568-1.568a1.026 1.026 0 0 0-.877-.29c-.493.074-.84.504-1.02.968a2.5 2.5 0 1 1-3.237-3.237c.464-.18.894-.527.967-1.02a1.026 1.026 0 0 0-.289-.877l-1.568-1.568A2.402 2.402 0 0 1 1.998 12c0-.617.236-1.234.706-1.704L4.315 8.685a.98.98 0 0 1 .837-.276c.47.07.802.48.968.925a2.501 2.501 0 1 0 3.214-3.214c-.446-.166-.855-.497-.925-.968a.979.979 0 0 1 .276-.837l1.61-1.61a2.404 2.404 0 0 1 1.705-.707 2.402 2.402 0 0 1 1.704.706l1.568 1.568c.23.23.556.338.877.29.493-.074.84-.504 1.02-.968a2.5 2.5 0 1 1 3.237 3.237c-.464.18-.894.527-.967 1.02Z"/>',
  'globe': '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
  'shield': '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>',
}

function lucideIconSvg(name: string): string {
  const paths = LUCIDE_SVG[name]
  if (!paths) return `[icon:${name}]` // Unknown icon — leave as text
  return `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-2px;margin-right:3px;opacity:0.7">${paths}</svg>`
}

export type PillAction = 'link' | 'need' | 'chat'

export interface SuggestionPill {
  label: string
  prompt: string
  action: PillAction
  /** For link pills: entity names to resolve */
  source?: string
  target?: string
  /** For need pills: the need statement to create */
  needStatement?: string
}

/** Extract clickable next-step pills from response text */
export function extractSuggestionPills(content: string): SuggestionPill[] {
  const pills: SuggestionPill[] = []

  // 1. Link suggestions: "Source" → Target
  const linkMatches = content.matchAll(/"([^"]{3,40})"\s*→\s*\*{0,2}([^*\n(]{3,40})\*{0,2}/g)
  for (const m of linkMatches) {
    if (pills.length >= 4) break
    const source = m[1].trim()
    const target = m[2].trim().replace(/\*+/g, '')
    pills.push({
      label: `${truncate(source, 18)} → ${truncate(target, 18)}`,
      prompt: `Create a cross-link from "${source}" to "${target}"`,
      action: 'link',
      source,
      target,
    })
  }

  // 2. Need creation: **CUSTOMERNEED: Title** or **CustomerNeed: Title**
  const needMatches = content.matchAll(/\*{0,2}(?:CUSTOMERNEED|CustomerNeed|Need):\s*([^*\n]{5,60})\*{0,2}/gi)
  for (const m of needMatches) {
    if (pills.length >= 6) break
    const statement = m[1].trim()
    pills.push({
      label: `+ Need: ${truncate(statement, 25)}`,
      prompt: '',
      action: 'need',
      needStatement: statement,
    })
  }

  // 3. Fallback generic pills
  if (pills.length === 0) {
    if (content.includes('gap') || content.includes('unlinked')) {
      pills.push({ label: 'Create missing links', prompt: 'Help me create the missing cross-links you identified.', action: 'chat' })
    }
    if (content.includes('value prop') || content.includes('proposition')) {
      pills.push({ label: 'Refine value prop', prompt: 'Refine the value proposition further.', action: 'chat' })
    }
    if (content.includes('need') || content.includes('cluster')) {
      pills.push({ label: 'Synthesize needs', prompt: 'Create customer need entities from the pain clusters you identified. Format each as **CustomerNeed: statement**', action: 'chat' })
    }
  }

  return pills
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '...' : s
}
